import Button from 'devextreme-react/button';
import ScrollView from 'devextreme-react/scroll-view';
import ValidationGroup from 'devextreme-react/validation-group';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IDialogConfig } from '../../../../types/dialog';
import { IModalConfig } from '../../../../types/modal';
import ConfirmDialog from '../../../common/confirm/ConfirmDialog';
import icons from '../../../common/icons/icons';
import Modal from '../../../common/modal/Modal';
import VesselHandledEditForm from './VesselHandledEditForm';
import { MarsApiService as api } from '../../../../api/mars-api-service';
import { MarsApiConfig as apiConfig } from '../../../../api/mars-api-config';
import toast from '../../../../utils/toast';
import { IEditVesselHandled } from '../../../../types/port-configurations';

export default function VesselHandledEditModal(props: {
  isVisible: boolean;
  handleCancelClick: any;
  getVesselHandled: any;
  terminalCode: any;
  vesselHandledId?: any;
}) {
  const { t } = useTranslation();
  const vesselHandledRef = useRef<any>();
  const {
    handleCancelClick,
    isVisible,
    getVesselHandled,
    terminalCode,
    vesselHandledId,
  } = props;
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [vesselType, setVesselType] = useState<any>([]);
  const [selectedVesselHandle, setSelectedVesselHandle] = useState<any>([]);
  const [showConfirmBack, setShowConfirmBack] = useState(false);

  const modalConfig: IModalConfig = {
    width: 450,
    height: 310,
    visible: isVisible,
    showTitle: false,
    closeOnOutsideClick: false,
  };

  const getVesselTypeDropDown = async () => {
    await api.get({ url: apiConfig.getShipType }).then((data: any) => {
      setVesselType(data);
    });
  };

  useEffect(() => {
    getVesselTypeDropDown();
  }, []);

  const popupAttributes = useMemo(() => {
    return {
      id: 'elementId',
      class: 'm-l-modal-main__wrap m-l-edit-name-popup',
    };
  }, []);

  const confirmSaveDialogConfig: IDialogConfig = {
    id: 'confirmSave',
    handleSubmit: () => {
      onValueSubmit();
      setIsConfirmModalOpen(false);
    },
    handleClose: () => {
      setIsConfirmModalOpen(false);
    },
  };

  const formDefaultValues = {
    vesselType: null,
    vesselSize: '',
  };

  const vesselHandledForm = useForm({ defaultValues: formDefaultValues });
  const { isDirty } = vesselHandledForm.formState;
  const { getValues } = vesselHandledForm;
  let vesselHandledFormValues = getValues();

  const onCloseClick = () => {
    if (isDirty) {
      setShowConfirmBack(true);
    } else {
      vesselHandledForm.reset(formDefaultValues);
      setShowConfirmBack(false);
      handleCancelClick();
    }
  };

  const confirmBackDialogConfig: IDialogConfig = {
    id: 'confirmBack',
    content: t('toast.confirmBack'),
    handleSubmit: () => {
      setShowConfirmBack(false);
      vesselHandledForm.reset(formDefaultValues);
      handleCancelClick();
    },
    handleClose: () => {
      setShowConfirmBack(false);
    },
  };

  const onClickSave = () => {
    if (vesselHandledRef?.current?.instance.validate().isValid) {
      setIsConfirmModalOpen(true);
    }
  };

  const getSelectedVesselHandleValue = async () => {
    await api
      .get({
        url: apiConfig.vesselHandled,
        params: { terminalShipTypeHandleId: vesselHandledId },
      })
      .then((data: any) => {
        setSelectedVesselHandle(data);
      });
  };

  useEffect(() => {
    if (vesselHandledId) {
      getSelectedVesselHandleValue();
    }
  }, [vesselHandledId]);

  const onValueSubmit = async () => {
    vesselHandledFormValues = vesselHandledForm.getValues();
    const roleJsonEdit: IEditVesselHandled = {
      terminalShipTypeHandleId: vesselHandledId ? vesselHandledId : 0,
      shipType: vesselHandledFormValues.vesselType,
      shipSize: vesselHandledFormValues.vesselSize,
      terminalCode: terminalCode,
    };
    api
      .put({ url: apiConfig.vesselHandled, data: roleJsonEdit })
      .then((response) => {
        if (response) {
          toast.success({
            title: vesselHandledId
              ? t('toast.vesselHandleEditSuccess')
              : t('toast.vesselHandleAddSuccess'),
          });
          handleCancelClick();
          getVesselHandled(terminalCode);
        }
      });
  };

  useEffect(() => {
    vesselHandledForm.reset();
    if (selectedVesselHandle) {
      vesselHandledForm.reset(
        {
          vesselSize: selectedVesselHandle.shipSize,
          vesselType: selectedVesselHandle.shipType,
        },
        { keepDirty: true }
      );
    }
  }, [selectedVesselHandle]);

  return (
    <div className="m-l-model-popup__wrap" data-testid="editName">
      <Modal modalConfig={modalConfig} wrapperAttr={popupAttributes}>
        <div className="m-l-modal__header">
          <h2 className="modal-title">
            {vesselHandledId ? 'Edit Vessel Handled' : 'Add Vessel Handled'}
          </h2>
          <div className="m-l-modal-close-button-block">
            <Button
              className="app-c-btn app-c-icon-only-btn"
              onClick={onCloseClick}
            >
              <icons.IconCross />
            </Button>
          </div>
        </div>
        <div className="m-l-modal__body">
          <ScrollView width="100%" height="100%">
            <div className="m-l-edit-form-panel">
              <div className="row m-l-custom-sixteen-space-row">
                <FormProvider {...vesselHandledForm}>
                  <ValidationGroup ref={vesselHandledRef}>
                    <VesselHandledEditForm
                      vesselHandledRef={vesselHandledRef}
                      vesselType={vesselType}
                    />
                  </ValidationGroup>
                </FormProvider>
              </div>
            </div>
          </ScrollView>
        </div>
        <div className="m-l-modal__footer">
          <Button
            className="app-c-btn app-c-btn--secondary min-btn-width"
            elementAttr={{ 'data-testid': 'backButton' }}
            onClick={onCloseClick}
          >
            {t('buttons.cancel')}
          </Button>
          <Button
            className="app-c-btn app-c-btn--primary min-btn-width"
            elementAttr={{ 'data-testid': 'saveButton' }}
            onClick={onClickSave}
          >
            {t('buttons.save')}
          </Button>
          <ConfirmDialog
            dialogConfig={confirmSaveDialogConfig}
            isOpen={isConfirmModalOpen}
          />
          <ConfirmDialog
            dialogConfig={confirmBackDialogConfig}
            isOpen={showConfirmBack}
          />
        </div>
      </Modal>
    </div>
  );
}
